import { useEffect, useRef } from 'react'
import { ScrollArea, Stack, Text, Group, Badge, Box } from '@mantine/core'
import type { TimelineEntry, TimelineEntryType } from '../../hooks/useEtlActivity'

interface LiveTimelineProps {
  entries: TimelineEntry[]
  maxHeight?: number
}

function getEventColor(type: TimelineEntryType, success?: boolean): string {
  switch (type) {
    case 'job-started':
      return 'var(--color-info)'
    case 'job-completed':
      return success ? 'var(--color-success)' : 'var(--color-error)'
    case 'orchestrator-started':
      return 'var(--color-info)'
    case 'orchestrator-completed':
      return success ? 'var(--color-success)' : 'var(--color-error)'
    case 'entity-started':
      return 'var(--color-info)'
    case 'entity-completed':
      return 'var(--color-success)'
    case 'entity-failed':
      return 'var(--color-error)'
    case 'progress':
      return 'var(--color-text-secondary)'
    default:
      return 'var(--color-text-secondary)'
  }
}

function getEventIcon(type: TimelineEntryType, success?: boolean) {
  const size = 20
  const color = getEventColor(type, success)

  switch (type) {
    case 'job-started':
      // Rocket / play icon
      return (
        <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="8" fill={color} opacity="0.2" />
          <path d="M8 6L14 10L8 14V6Z" fill={color} />
        </svg>
      )
    case 'job-completed':
      // Flag icon
      return (
        <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="8" fill={color} opacity="0.2" />
          {success ? (
            <path d="M6 10L9 13L14 7" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          ) : (
            <path d="M7 7L13 13M13 7L7 13" stroke={color} strokeWidth="2" strokeLinecap="round" />
          )}
        </svg>
      )
    case 'orchestrator-started':
    case 'orchestrator-completed':
      // Gear icon
      return (
        <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="4" stroke={color} strokeWidth="1.5" fill="none" />
          <path d="M10 2V5M10 15V18M2 10H5M15 10H18" stroke={color} strokeWidth="1.5" />
        </svg>
      )
    case 'entity-started':
      // Arrow right icon
      return (
        <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="8" fill={color} opacity="0.15" />
          <path d="M8 6L13 10L8 14" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )
    case 'entity-completed':
      // Check icon
      return (
        <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="8" fill={color} opacity="0.2" />
          <path d="M6 10L9 13L14 7" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )
    case 'entity-failed':
      // X icon
      return (
        <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="8" fill={color} opacity="0.2" />
          <path d="M7 7L13 13M13 7L7 13" stroke={color} strokeWidth="2" strokeLinecap="round" />
        </svg>
      )
    default:
      return (
        <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="4" fill={color} opacity="0.3" />
        </svg>
      )
  }
}

function getBadgeColor(type: TimelineEntryType): string {
  switch (type) {
    case 'job-started':
    case 'job-completed':
      return 'violet'
    case 'orchestrator-started':
    case 'orchestrator-completed':
      return 'blue'
    case 'entity-started':
    case 'entity-completed':
      return 'teal'
    case 'entity-failed':
      return 'red'
    default:
      return 'gray'
  }
}

function getBadgeLabel(type: TimelineEntryType): string {
  switch (type) {
    case 'job-started':
      return 'Job Start'
    case 'job-completed':
      return 'Job End'
    case 'orchestrator-started':
      return 'Module Start'
    case 'orchestrator-completed':
      return 'Module End'
    case 'entity-started':
      return 'Entity Start'
    case 'entity-completed':
      return 'Entity Done'
    case 'entity-failed':
      return 'Entity Fail'
    case 'progress':
      return 'Progress'
    default:
      return 'Event'
  }
}

function formatTime(timestamp: string): string {
  const date = new Date(timestamp)
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

export function LiveTimeline({ entries, maxHeight = 400 }: LiveTimelineProps) {
  const viewportRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom when new entries arrive
  useEffect(() => {
    if (viewportRef.current) {
      viewportRef.current.scrollTo({
        top: viewportRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }
  }, [entries.length])

  const lineColor = 'var(--color-border)'

  if (entries.length === 0) {
    return (
      <Box
        style={{
          height: 200,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
        }}
      >
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
          <circle cx="24" cy="24" r="20" stroke="var(--color-border)" strokeWidth="2" strokeDasharray="4 4" fill="none" />
          <path d="M24 14V26" stroke="var(--color-text-secondary)" strokeWidth="2" strokeLinecap="round" />
          <path d="M24 26L30 32" stroke="var(--color-text-secondary)" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <Text size="sm" c="dimmed" ta="center">
          Waiting for ETL events...
        </Text>
        <Text size="xs" c="dimmed" ta="center">
          Real-time events will appear here when a job runs.
        </Text>
      </Box>
    )
  }

  return (
    <ScrollArea h={maxHeight} type="auto" offsetScrollbars viewportRef={viewportRef}>
      <Stack gap="md" pr="xs">
        {entries.map((entry, index) => (
          <Group key={entry.id} gap="sm" align="flex-start" style={{ position: 'relative' }}>
            {/* Timeline connector line */}
            {index < entries.length - 1 && (
              <Box
                style={{
                  position: 'absolute',
                  left: 9,
                  top: 24,
                  width: 2,
                  height: 'calc(100% + 8px)',
                  background: lineColor,
                  opacity: 0.5,
                }}
              />
            )}
            {/* Icon */}
            <Box style={{ zIndex: 1, flexShrink: 0 }}>
              {getEventIcon(entry.type, entry.success)}
            </Box>
            {/* Content */}
            <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
              <Group justify="space-between" gap="xs" wrap="nowrap">
                <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                  <Text size="sm" fw={500} truncate>
                    {entry.title}
                  </Text>
                  <Badge color={getBadgeColor(entry.type)} variant="light" size="xs">
                    {getBadgeLabel(entry.type)}
                  </Badge>
                </Group>
                <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {formatTime(entry.timestamp)}
                </Text>
              </Group>
              <Text size="xs" c="dimmed" lineClamp={2}>
                {entry.description}
              </Text>
            </Stack>
          </Group>
        ))}
      </Stack>
    </ScrollArea>
  )
}
